import React, { useState, useEffect, useCallback } from 'react';
import { useFormik } from 'formik';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import Spinner from '../common/Spinner';

import InputText from '../common/InputText';
import Badge from '../common/Badge';
import {
  addGiftCard,
  removeGiftCard,
  removeGiftCardBalance,
  removeGiftCardError,
  getGiftCardBalance,
  removeGiftCardsFromCart,
} from '../../slices/cart';
import { formatGiftCardCode, parseGiftCardBalance } from '../../utils';
import styles from './GiftCard.module.scss';
import validator from './validator';
import messages from './messages';
import {
  GIFT_CARD_ZERO_BALANCE,
  GIFT_CARD_BALANCE_ERROR,
  DUPLICATE_GIFT_CARD,
  GIFT_CARD_MAX_APPLIED,
} from './constants';

import { PAYMENT_ERROR_DECLINED_GIFT_CARD } from '../../blocks/Checkout/SteppedCheckout/constants';

import { FAILED_ORDER_STATUS } from '../../utils/common/constants';
import Tooltip from '../common/Tooltip/Tooltip';

const getErrorMessage = (errorCode) => {
  switch (errorCode) {
    case DUPLICATE_GIFT_CARD:
      return messages.giftCardAlreadyApplied;
    case GIFT_CARD_ZERO_BALANCE:
      return messages.giftCardZeroBalance;
    case GIFT_CARD_MAX_APPLIED:
      return messages.giftCardMaxApplied;
    case GIFT_CARD_BALANCE_ERROR:
    default:
      return messages.giftCardDoesNotExist;
  }
};

const initialValues = {
  giftCardNumber: '',
  giftCardPin: '',
};

const GiftCard = () => {
  const dispatch = useDispatch();
  const { formatMessage, formatNumber } = useIntl();

  const cart = useSelector((state) => state.cart);
  const giftCardsApplied = useSelector((state) => state.cart.giftCards);
  const giftCardBalanceAmount = useSelector(
    (state) => state.cart.balanceAmount,
  );
  const order = useSelector((state) => state.order);

  const giftCardErrorCode = useSelector(
    (state) => state.cart.giftCardErrorCode,
  );

  const [isExpanded, setExpanded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const saveGiftCard = async (giftCardValues) => {
    setIsSaving(true);
    await dispatch(
      addGiftCard({
        giftCardNumber: giftCardValues.giftCardNumber,
        securityCode: giftCardValues.giftCardPin,
      }),
    );
    setIsSaving(false);
  };

  const {
    values, errors, handleChange, handleBlur, resetForm, isValid, handleSubmit, setErrors
  } =
    useFormik({
      initialValues,
      validate: (v) => validator(v, { formatMessage, isExpanded, formSubmitted: false }),
      onSubmit: async (v, { setSubmitting }) => {

        const giftCardFormrrors = validator(v, { formatMessage, isExpanded, formSubmitted: true });

        const formErrors = { ...errors, ...giftCardFormrrors };

        if (Object.keys(formErrors).length === 0) {
          saveGiftCard(v);
        } else {
          setErrors(formErrors);
        }
        setSubmitting(false);
      },
      validateOnChange: true,
      validateOnBlur: false,
    });

  const deleteGiftCard = (giftCardCode) =>
    dispatch(
      removeGiftCard({
        giftCardCode,
      }),
    );

  useEffect(() => {
    if (giftCardsApplied?.length > 0) {
      resetForm({ values: initialValues });
      dispatch(removeGiftCardBalance());
      setExpanded(false);
    }
  }, [dispatch, giftCardsApplied?.length, resetForm]);

  useEffect(() => {
    if (!errors && isValid && isExpanded && values.giftCardNumber && values.giftCardPin) {
      dispatch(
        getGiftCardBalance({
          giftCardNumber: values.giftCardNumber,
          securityCode: values.giftCardPin,
        }),
      );
    } else {
      dispatch(removeGiftCardBalance());
      dispatch(removeGiftCardError());
    }
  }, [
    dispatch,
    isExpanded,
    isValid,
    values.giftCardNumber,
    values.giftCardPin,
    errors
  ]);

  useEffect(() => {
    if (!isExpanded && giftCardErrorCode) {
      dispatch(removeGiftCardError());
    }
  }, [dispatch, giftCardErrorCode, isExpanded]);

  useEffect(() => {
    if (
      order.status === FAILED_ORDER_STATUS &&
      order.errorCode === PAYMENT_ERROR_DECLINED_GIFT_CARD &&
      giftCardsApplied.length
    ) {
      dispatch(removeGiftCardsFromCart());
    }
  }, [dispatch, giftCardsApplied, order.errorCode, order.status]);

  const toggleGiftCard = () => setExpanded((prevExpanded) => !prevExpanded);

  const renderGiftCardPinLabel = useCallback(
    () => (
      <div className={styles.tooltip}>
        {formatMessage(messages.giftCardPinLabel)}
        <Tooltip
          direction="right-edge"
          align="top"
          content={formatMessage(messages.giftCardTooltip)}
        >
          <div
            className={styles['info-icon']}
            onClick={(e) => e.preventDefault()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
              }
            }}
            role="button"
            aria-label="info-icon"
            tabIndex="0"
          />
        </Tooltip>
      </div>
    ),
    [formatMessage],
  );

  return (
    <div className={styles['gift-card-container']}>
      <div className={styles['gift-card-code']}>
        <div className={styles['gift-card-title']}>
          {formatMessage(messages.giftCardTitle)}
        </div>

        <span
          onClick={toggleGiftCard}
          data-testid="add-button"
          role="link"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              toggleGiftCard()
            }
          }}
          className={styles['gift-card-code-amount']}
          tabIndex={0}
        >
          {!isExpanded
            ? formatMessage(messages.add)
            : formatMessage(messages.hide)}
        </span>
      </div>
      {isExpanded && (
        <form
          onSubmit={handleSubmit}
          // onSubmit={saveGiftCard}
          className={styles['gift-card-code-expanded']}
        >
          <div className={styles['gift-card-inputs']}>
            <div className={styles['gift-card-code-input']}>
              <InputText
                name="giftCardNumber"
                label={formatMessage(messages.giftCardNumberLabel)}
                placeholder={formatMessage(messages.giftCardPlaceHolder)}
                value={values.giftCardNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                errorMsg={errors.giftCardNumber}
              />
            </div>
            <div className={styles['gift-card-pin-input']}>
              <InputText
                name="giftCardPin"
                label={renderGiftCardPinLabel()}
                placeholder={formatMessage(messages.giftCardPinPlaceHolder)}
                value={values.giftCardPin}
                onChange={handleChange}
                onBlur={handleBlur}
                errorMsg={errors.giftCardPin}
                buttonText={formatMessage(messages.applyButton)}
              />
            </div>
          </div>
          {giftCardBalanceAmount && (
            <div className={styles['gift-card-title']}>
              {`${formatMessage(messages.giftCardBalanceSubTitle)}
                ${parseGiftCardBalance({
                balanceAmount: giftCardBalanceAmount,
                formatNumber,
                formatMessage,
              })}`}
            </div>
          )}
          {cart?.giftCardErrorCode && (
            <div
              className={styles['gift-card-error']}
              data-testid="gift-card-error"
            >
              {formatMessage(getErrorMessage(cart?.giftCardErrorCode), {
                code: values.giftCardNumber,
              })}
            </div>
          )}
        </form>
      )}
      {giftCardsApplied?.length > 0 && (
        <div
          className={styles['gift-cards-applied-container']}
          data-testid="gift-cards-container"
        >
          {giftCardsApplied.map((giftCardApplied) => (
            <Badge
              text={`${formatGiftCardCode(giftCardApplied.giftCardNumber)}
              - ${parseGiftCardBalance({
                balanceAmount: giftCardApplied.amount,
                formatNumber,
                formatMessage,
              })}`}
              key={giftCardApplied.giftCardNumber}
              onClickButton={() =>
                deleteGiftCard(giftCardApplied.giftCardNumber)}
            />
          ))}
        </div>
      )}
      {isSaving && (
        <Spinner />
      )}
    </div>
  );
};

export default GiftCard;
